import type { ScrollEventProtocol } from '@lighthouse/core'
import type { RefObject } from 'react'
import { useCallback, useEffect, useRef } from 'react'

import { useActionTrigger } from '@/hooks/useActionTrigger'

interface UseScrollEventPayload {
    ref: RefObject<HTMLDivElement>
    scrollBottomEvent: ScrollEventProtocol | undefined
}

export const useScrollEvent = (payload: UseScrollEventPayload) => {
    const { handleActionListTrigger } = useActionTrigger()
    const { ref, scrollBottomEvent } = payload
    const wasInTriggerZoneRef = useRef(false)
    const { actions, config } = scrollBottomEvent ?? {}
    const enableScrollEvent = !!actions && actions.length > 0

    const handleScrollEvent = useCallback(
        (ev: Event) => {
            if (!enableScrollEvent || !scrollBottomEvent) {
                return
            }
            const bottom = config?.bottom ?? 0
            const { scrollHeight, scrollTop, clientHeight } = ev.target as HTMLDivElement
            const isInTriggerZone = scrollHeight - scrollTop - clientHeight <= bottom

            // 边缘触发：只在从"不在区域"变为"在区域"时触发
            if (!wasInTriggerZoneRef.current && isInTriggerZone) {
                handleActionListTrigger(scrollBottomEvent.id, scrollBottomEvent)
            }

            // 更新状态
            wasInTriggerZoneRef.current = isInTriggerZone
        },
        [config?.bottom, enableScrollEvent, handleActionListTrigger, scrollBottomEvent]
    )

    useEffect(() => {
        const el = ref.current
        if (!el || !enableScrollEvent) {
            return
        }

        // 配置变化时重置状态
        wasInTriggerZoneRef.current = false

        el.addEventListener('scroll', handleScrollEvent)
        return () => {
            el.removeEventListener('scroll', handleScrollEvent)
        }
    }, [enableScrollEvent, ref, handleScrollEvent])
}
